// ConsiderSupport.jsx (camelCase-ready)
import React, { useEffect, useMemo, useState } from "react";
import {
    Container, Row, Col,
    Card, CardBody, CardHeader, FormGroup, Label,
    Badge, Table, Spinner, Alert, Button
} from "reactstrap";
import { FiRefreshCw } from "react-icons/fi";
import { IoDocumentTextOutline } from "react-icons/io5";
import { MdBusiness, MdPerson } from "react-icons/md";
import { FaSistrix } from "react-icons/fa";

const SUPPORT_TYPE_MAP = {
    1: "ประกาศข้อ 4",
    2: "ประกาศข้อ 5",
    4: "ประกาศข้อ 6",
    5: "ประกาศข้อ 7",
    6: "ประกาศข้อ 8",
    3: "ประกาศข้อ 10 ",
    9: "ประกาศข้อ 10 ",
};

const typeToColor = (t) => ({
    1: "primary",
    2: "success",
    4: "info",
    5: "warning",
    6: "secondary",
    3: "dark",
    9: "dark",
}[t] || "secondary");

const Line = ({ label, value }) => (
    <div className="d-flex justify-content-between align-items-start py-1">
        <div className="text-muted">{label}</div>
        <div className="fw-semibold text-end ms-3" style={{ maxWidth: 360 }}>{value ?? "-"}</div>
    </div>
);

const SupportHeader = ({ data, onRefresh, loading }) => {
    const title = data?.supportName || "—";
    const typeId = data?.supportTypeId;
    const badgeText = SUPPORT_TYPE_MAP[typeId] || `ประเภท ${typeId ?? "-"}`;

    return (
        <Card className="shadow-sm border-0">
            <CardHeader
                className="border-0"
                style={{ background: "linear-gradient(90deg, rgba(14,165,233,0.12) 0%, rgba(99,102,241,0.12) 100%)" }}
            >
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <IoDocumentTextOutline size={22} className="text-primary" />
                        <h5 className="m-0">{title}</h5>
                        {typeId ? <Badge color={typeToColor(typeId)} pill className="ms-1">{badgeText}</Badge> : null}
                    </div>

                    <Button color="light" disabled={loading} onClick={onRefresh}>
                        <FiRefreshCw className={loading ? "spin" : ""} />
                        <span className="ms-2">รีเฟรช</span>
                    </Button>
                </div>
            </CardHeader>

            <CardBody>
                <Row className="g-3">
                    <Col md="6">
                        <Card className="border-0 shadow-sm h-100">
                            <CardHeader className="bg-white border-0 pb-0">
                                <div className="d-flex align-items-center gap-2">
                                    <MdBusiness className="text-info" />
                                    <h6 className="m-0">ข้อมูลผู้ขอรับเงินช่วยเหลือหรืออุดหนุน</h6>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Line label="ชื่อผู้ขอ" value={data?.supportName} />
                                <Line label="เลขทะเบียนนิติบุคคล" value={data?.customerNo} />
                                <Line label="เลขสถานประกอบการ" value={data?.companyNo} />
                                <Line label="ประเภทธุรกิจ" value={data?.businessType} />

                                {/* <Line label="ปีงบประมาณ" value={data?.budgetYear} /> */}
                                <Line label="จำนวนลูกจ้างทั้งหมด (คน)" value={data?.employeeAmount} />
                                <Line label="สั่งจ่ายในนาม" value={data?.paymentName} />
                                <Line label="เลขที่ใบอนุญาตการเป็นผู้ดำเนินการทดสอบ ตามมาตรา 24" value={data?.testLicense24No} />
                                {/* <Line label="ใบรับรอง 264" value={data?.certificate264} /> */}
                                {/* <Line label="ปีข้อมูล (year1)" value={data?.year1} /> */}
                                {/* <Line
                                    label="วันเริ่ม/สิ้นสุดใบรับรอง"
                                    value={`${data?.startDateCertificate ?? "-"} - ${data?.endDateCertificate ?? "-"}`}
                                /> */}
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md="6">
                        <Card className="border-0 shadow-sm h-100">
                            <CardHeader className="bg-white border-0 pb-0">
                                <div className="d-flex align-items-center gap-2">
                                    <MdPerson className="text-success" />
                                    <h6 className="m-0">ผู้รับมอบอำนาจ</h6>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Line label="ชื่อ-สกุล" value={data?.authFullName} />
                                <Line label="เลขบัตรประชาชน" value={data?.authPersonalId} />
                                <Line
                                    label="ที่อยู่"
                                    value={[
                                        data?.authAddressNo ? `เลขที่ ${data?.authAddressNo}` : null,
                                        data?.authAddressMoo ? `หมู่ ${data?.authAddressMoo}` : null,
                                        data?.authAddressSoi ? `ซอย ${data?.authAddressSoi}` : null,
                                        data?.authAddressStreet ? `ถนน ${data?.authAddressStreet}` : null,
                                        data?.authAddressTumbonName ? `ต.${data?.authAddressTumbonName}` : null,
                                        data?.authAddressAmphurName ? `อ.${data?.authAddressAmphurName}` : null,
                                        data?.authAddressProvinceName ? `จ.${data?.authAddressProvinceName}` : null,
                                        data?.authAddressPostCode ? `${data?.authAddressPostCode}` : null,
                                    ].filter(Boolean).join(" ")}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

const SimpleTable = ({ columns, rows, keyField = "id" }) => (
    <div className="table-responsive">
        <Table hover bordered className="align-middle">
            <thead className="table-light">
                <tr>{columns.map((c) => <th key={c.key} className={c.thClassName}>{c.header}</th>)}</tr>
            </thead>
            <tbody>
                {rows?.length ? rows.map((r, idx) => (
                    <tr key={r[keyField] ?? `${idx}`}>
                        {columns.map((c) => (
                            <td key={c.key} className={c.tdClassName}>
                                {c.render ? c.render(r[c.key], r, idx) : (r[c.key] ?? "-")}
                            </td>
                        ))}
                    </tr>
                )) : (
                    <tr><td colSpan={columns.length} className="text-center text-muted py-4">ไม่มีข้อมูล</td></tr>
                )}
            </tbody>
        </Table>
    </div>
);

/** ประกาศ 5: ใช้ field ใน data.detailView (camelCase อยู่แล้วจาก backend) */
const DetailAnnounce5 = ({ view }) => {
    const items = [
        { label: "ปี พ.ศ. (year1)", value: view?.year1 },
        { label: "ปี พ.ศ. (year2)", value: view?.year2 },
        { label: "เลขทะเบียน (rigNo)", value: view?.rigNo },
        { label: "วันที่รับใบเสร็จ", value: view?.receiptDate },
        { label: "จำนวนลูกจ้างเฉลี่ยรวมทั้งสิ้น", value: view?.avgMan },
        { label: "ฝึกอบรม (train)", value: view?.train },
        { label: "ทดสอบมาตรฐาน (test)", value: view?.test },
        { label: "รับรองความรู้ความสามารถ (oloc)", value: view?.oloc },
        { label: "ตามกฎหมาย (lawTrainMan)", value: view?.lawTrainMan },
        { label: "ร้อยละ (perc)", value: view?.perc },
        { label: "จำนวนเงิน (amount)", value: view?.amount },
        { label: "รวมดำเนินการพัฒนาฯ (spkTrainMan)", value: view?.spkTrainMan },
    ];

    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0"><h6 className="m-0">รายละเอียด: ประกาศ 5 (ใหม่)</h6></CardHeader>
            <CardBody>
                <Row>
                    {items.map((it, i) => (
                        <Col md="6" key={i} className="mb-2">
                            <div className="small text-muted">{it.label}</div>
                            <div className="fw-semibold">{it.value ?? "-"}</div>
                        </Col>
                    ))}
                </Row>
            </CardBody>
        </Card>
    );
};

/** ประกาศ 7 (supportTypeId = 5) */
const DetailAnnounce7 = ({ list }) => {
    const columns = [
        { key: "cer2Num", header: "เลข รมฐ.2" },
        { key: "careerName", header: "สาขาอาชีพ" },
        { key: "occupationName", header: "สาขา" },
        { key: "occupationLevel", header: "ระดับ" },
        { key: "cer2ApproveDate", header: "วันที่ออก" },
        { key: "personTotal", header: "จำนวนทดสอบ (คน)" },
        { key: "employeeTotal", header: "ลูกจ้างในตำแหน่งงาน (คน)" },
    ];
    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0"><h6 className="m-0">รายละเอียด: ประกาศ 7 (ใหม่)</h6></CardHeader>
            <CardBody><SimpleTable columns={columns} rows={list} /></CardBody>
        </Card>
    );
};

/** ประกาศ 8 (supportTypeId = 6) */
const DetailAnnounce8 = ({ list }) => {
    const columns = [
        { key: "fullName", header: "ชื่อ-สกุล" },
        { key: "lawOccupationName", header: "สาขาอาชีพ" },
        { key: "testOccupationName", header: "สาขา " },
        { key: "testOccupationLevel", header: "ระดับ" },
        { key: "payRate", header: "อัตราค่าจ้าง" },
        { key: "payRateAmount", header: "จำนวนเงิน" },
        { key: "payRateStartDate", header: "เริ่มจ่ายค่าจ้าง" },
        { key: "payRateEndDate", header: "งวดสุดท้าย" },
        { key: "certificateNo", header: "เลขที่หนังสือรับรอง" },
    ];
    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0"><h6 className="m-0">รายละเอียด: ประกาศ 8 (ใหม่)</h6></CardHeader>
            <CardBody><SimpleTable columns={columns} rows={list} /></CardBody>
        </Card>
    );
};

const openInNewTab = (url) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
}

function onClickUrl(url) {
    return () => openInNewTab(url)
}
/** ประกาศ 10 (supportTypeId = 3 หรือ 9) */
const DetailAnnounce10 = ({ list, variant }) => {
    const columns = [
        { key: "lawOccupationName", header: "สาขาอาชีพ " },
        { key: "testOccupationName", header: "สาขา" },
        { key: "personTotal", header: "จำนวนคนทั้งหมด" },
        { key: "receiptNo", header: "เลขที่ใบเสร็จ" },
        { key: "receipt", header: "ใบเสร็จเล่มที่" },
        { key: "receiptDate", header: "วันที่ใบเสร็จ" },
        { key: "certificateNo", header: "เลขที่หนังสือรับรอง" },
        { key: "amountTotal", header: "จำนวนเงินรวม" },
    ];
    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0"><h6 className="m-0">รายละเอียด: ประกาศ 10 ({variant})</h6></CardHeader>
            <CardBody><SimpleTable columns={columns} rows={list} /></CardBody>
        </Card>
    );
};

const DetailPlaceholder = ({ label = "รายละเอียด" }) => (
    <Alert color="light" className="border-0 shadow-sm">{label} ยังไม่พร้อมแสดงผล หรือไม่มีข้อมูลในระบบ</Alert>
);

export function DetailSupport({ supportId }) {
    const [data, setData] = useState(null);
    console.log("data", data);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const fetchData = async () => {
        try {
            setLoading(true);
            setErr("");
            const res = await fetch(`/api/SupportForm/detailSupport/${supportId}`, {
                headers: { "Accept": "application/json" },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            // รองรับทั้ง { data: {...} } / { dataenum: {...} } / {...}
            const payload = json?.data ?? json?.dataenum ?? json;
            setData(payload);
        } catch (e) {
            console.error(e);
            setErr("ไม่สามารถโหลดข้อมูลได้");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (supportId) fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supportId]);

    const typeId = data?.supportTypeId;
    const typeLabel = useMemo(() => SUPPORT_TYPE_MAP[typeId] || "", [typeId]);

    return (
        <Container className="py-3">
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 180 }}>
                    <Spinner /> <span className="ms-2">กำลังโหลดข้อมูล…</span>
                </div>
            ) : err ? (
                <Alert color="danger" className="shadow-sm">
                    {err}
                    <div className="mt-2"><Button color="light" size="sm" onClick={fetchData}>ลองใหม่</Button></div>
                </Alert>
            ) : (
                <>
                    <SupportHeader data={data} onRefresh={fetchData} loading={loading} />
                    <div className="my-3" />
                    {typeId === 2 && <DetailAnnounce5 view={data?.detailView} />}
                    {typeId === 5 && <DetailAnnounce7 list={data?.detail} />}
                    {typeId === 6 && <DetailAnnounce8 list={data?.detail} />}
                    {(typeId === 3 || typeId === 9) && (
                        <DetailAnnounce10
                            list={data?.detail}
                            variant={typeId === 3 ? "ศูนย์ทดสอบ" : "ศูนย์ประเมิน"}
                        />
                    )}
                    {typeId === 1 && <DetailPlaceholder label="รายละเอียด: ประกาศ 4" />}
                    {typeId === 4 && <DetailPlaceholder label="รายละเอียด: ประกาศ 6" />}



                    <div className="border border-1 w-100">
                        {data?.fIdCardPathView != null || data?.fOtherPathView != null || data.fCertificatePathView != null || data.fSsoPathView != null ? (
                            <>
                                <p className="text-center text-white" style={{ lineHeight: "3rem", backgroundColor: "#0A66C2" }}>ข้อมูลเพิ่มเติมและเอกสารประกอบการยื่น</p>
                                <div className="d-flex flex-column">
                                    {data?.fIdCardPathView != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">บัตรประจำตัวประชาชน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fIdCardPathView}`)} />
                                        </FormGroup>
                                    ) : null}
                                    {data?.fOtherPathView != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">เอกสารอื่นๆ</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fOtherPathView}`)} />
                                        </FormGroup>
                                    ) : null}
                                    {data?.fCertificatePathView != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">หนังสือรับรองมาตรฐานฝีมือแรงงาน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fCertificatePathView}`)} />
                                        </FormGroup>
                                    ) : null}

                                    {data?.fSsoPathView != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">หนังสือรายการส่งเงินสบทบทุน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fSsoPathView}`)} />
                                        </FormGroup>
                                    ) : null}
                                    {data?.fEmployeeCertTotal != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">เอกสารที่แสดงจำนวนลูกจ้างทั้งหมดในตำแหน่งงานที่ดำเนินการทดสอบมาตรฐานฝีมือแรงงาน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fEmployeeCertTotal}`)} />
                                        </FormGroup>
                                    ) : null}
                                    {data?.fCertificateTest != null ? (
                                        <FormGroup className='d-flex justify-content-between'>
                                            <Label className="px-3">หนังสือรับรองว่าเป็นผู้ผ่านการทดสอบมาตรฐานฝีมือแรงงาน</Label>
                                            <FaSistrix className="mx-3 style-icon" onClick={onClickUrl(`${data.fCertificateTest}`)} />
                                        </FormGroup>
                                    ) : null}
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-center text-white" style={{ lineHeight: "3rem", backgroundColor: "#0A66C2" }}>ข้อมูลเพิ่มเติมและเอกสารประกอบการยื่น</p>
                                <div className="d-flex flex-column align-items-center justify-content-center p-2">
                                    ไม่มีเอกสารประกอบการยื่นเพิ่มเติม
                                </div>
                            </>
                        )}



                    </div>






                </>
            )}
        </Container>
    );
}

// เพิ่ม CSS สำหรับไอคอนโหลด
// .spin { animation: spin 1.1s linear infinite; }
// @keyframes spin { to { transform: rotate(360deg); } }
